import type { ReactNode } from "react";
import { UtilityMessage } from "../../../shared/components/UtilityStates";
import { ConsumptionFilterBar } from "../../../shared/consumption/components/ConsumptionFilterBar";
import { ConsumptionReport } from "../../../shared/consumption/components/ConsumptionReport";
import { ConsumptionError } from "../../../shared/consumption/components/ConsumptionStates";
import type { GasZoneSelection } from "../../../shared/consumption/hooks/useGasConsumptionZones";
import type { UtilityConsumptionReportRange } from "../../../shared/consumption/models/UtilityConsumption";
import {
	GAS_ZONE_TREE_LABELS,
	getGasZoneLabel,
	toZoneMeters,
} from "../../../shared/consumption/utils/gasConsumption";
import { UTILITY_KIND } from "../../../shared/models/Utility";

interface GasReportProps {
	targetId: number | null;
	zoneSelection: GasZoneSelection;
	areaSelector?: ReactNode;
	range: UtilityConsumptionReportRange;
	onRangeChange: (range: UtilityConsumptionReportRange) => void;
}

export const GasReport = ({
	targetId,
	zoneSelection,
	areaSelector,
	range,
	onRangeChange,
}: GasReportProps) => {
	const {
		zones,
		hasZones,
		zoneError,
		isZoneError,
		isZoneLoading,
		refetchZones,
		treeSelectedKeys,
		requestZoneKeys,
		isEmptySelection,
		areZonesLoaded,
		changeZoneKeys,
	} = zoneSelection;

	const zoneByKey = new Map((zones ?? []).map((zone) => [zone.ZoneKey, zone]));
	const getZoneLabel = (meter: { ExternalMeterId: string }) => {
		const zone = zoneByKey.get(meter.ExternalMeterId);
		return zone === undefined ? meter.ExternalMeterId : getGasZoneLabel(zone);
	};

	if (isZoneError || (areZonesLoaded && !hasZones)) {
		return (
			<>
				<ConsumptionFilterBar
					meters={[]}
					isMeterLoading={false}
					selectedMeterIds={null}
					meterLabels={GAS_ZONE_TREE_LABELS}
					areaSelector={areaSelector}
					onMeterChange={changeZoneKeys}
				/>
				{isZoneError ? (
					<ConsumptionError
						subject="Furnace gas zones"
						message={zoneError?.message ?? "Unexpected response."}
						onRetry={() => refetchZones()}
					/>
				) : (
					<UtilityMessage
						title="No furnace gas"
						detail="This area has no furnace, so no gas is measured for it."
					/>
				)}
			</>
		);
	}

	return (
		<ConsumptionReport
			targetId={targetId}
			utility={UTILITY_KIND.GAS}
			meters={toZoneMeters(zones ?? [])}
			isMeterLoading={isZoneLoading}
			selectedMeterIds={treeSelectedKeys}
			resolvedMeterIds={requestZoneKeys}
			areMetersLoaded={areZonesLoaded}
			isEmptySelection={isEmptySelection}
			range={range}
			areaSelector={areaSelector}
			meterLabels={GAS_ZONE_TREE_LABELS}
			getMeterItemLabel={getZoneLabel}
			showCost
			onMeterChange={changeZoneKeys}
			onRangeChange={onRangeChange}
		/>
	);
};
